import React, { useState, useEffect } from 'react';
import UDIRowPopup, { UDIPopupField, UDIAddRowButton, useUDIRowPopup } from './UDIRowPopup.js';

// SHC Types Dictionary (CommonDeviceType.xsd) - requiresComment codes need a value in Descriptions.
// Module-level (not per-render) so it can also be imported by ProductUDIs.js for the page-level save check.
export const shcTypes = [
    { key: "1", value: "SHC001", label: "Fragile, handle with care", requiresComment: false },
    { key: "2", value: "SHC002", label: "Keep away from sunlight", requiresComment: false },
    { key: "3", value: "SHC003", label: "Protect from heat and radioactive sources", requiresComment: false },
    { key: "4", value: "SHC004", label: "Protect from radioactive sources", requiresComment: false },
    { key: "5", value: "SHC005", label: "Keep dry", requiresComment: false },
    { key: "6", value: "SHC006", label: "Lower limit of temperature", requiresComment: true },
    { key: "7", value: "SHC007", label: "Upper limit of temperature", requiresComment: true },
    { key: "8", value: "SHC008", label: "Temperature limit", requiresComment: true },
    { key: "9", value: "SHC009", label: "Humidity limitation", requiresComment: true },
    { key: "10", value: "SHC010", label: "Atmospheric pressure limitation", requiresComment: true },
    { key: "11", value: "SHC012", label: "Dangerous voltage", requiresComment: false },
    { key: "12", value: "SHC013", label: "P401 – Store at…", requiresComment: true },
    { key: "13", value: "SHC014", label: "P402 – Store in a dry place", requiresComment: false },
    { key: "14", value: "SHC015", label: "P403 – Store in a well-ventilated place", requiresComment: false },
    { key: "15", value: "SHC016", label: "P404 – Store in a closed container", requiresComment: false },
    { key: "16", value: "SHC017", label: "P405 – Store locked up", requiresComment: false },
    { key: "17", value: "SHC018", label: "P406 – Store in corrosive resistant/… container with a resistant inner liner", requiresComment: false },
    { key: "18", value: "SHC019", label: "P407 – Maintain air gap between stacks/pallets", requiresComment: false },
    { key: "19", value: "SHC021", label: "P411 – Store at temperatures not exceeding … °C/…°F", requiresComment: true },
    { key: "20", value: "SHC022", label: "P412 – Do not expose to temperatures exceeding 50°C/122°F", requiresComment: true },
    { key: "21", value: "SHC023", label: "P413 – Store bulk masses greater than … kg/… lbs at temperatures not exceeding … °C/…°F", requiresComment: true },
    { key: "22", value: "SHC024", label: "P420 – Store away from other materials", requiresComment: false },
    { key: "23", value: "SHC025", label: "P422 – Store contents below …", requiresComment: true },
    { key: "24", value: "SHC033", label: "Electrostatic sensitive devices", requiresComment: false },
    { key: "25", value: "SHC035", label: "Do not sterilize", requiresComment: false },
    { key: "26", value: "SHC036", label: "Do not freeze", requiresComment: false },
    { key: "27", value: "SHC037", label: "Do not cut", requiresComment: false },
    { key: "28", value: "SHC038", label: "Do not store near magnets or magnetic devices", requiresComment: false },
    { key: "29", value: "SHC042", label: "Avoid contact with water", requiresComment: false },
    { key: "30", value: "SHC044", label: "This way up", requiresComment: false },
    { key: "31", value: "SHC045", label: "Period-after-opening …", requiresComment: true },
    { key: "32", value: "SHC046", label: "Special Storage Conditions (see IFU)", requiresComment: false },
    { key: "33", value: "SHC099", label: "OTHER", requiresComment: true },
];

// True if any item's type requires a comment but has no Descriptions - shared with ProductUDIs.js's
// page-level save validation, so both use the same rule instead of two copies drifting apart.
export const findMissingRequiredComment = (items) =>
    (items || []).filter(item => {
        const match = shcTypes.find(t => t.key === item.Type?.Value);
        return (match?.requiresComment ?? false) && (item.Descriptions || []).length === 0;
    });

const EUStorageAndHandlingConditionsList = ({
    UDI,
    setUDI,
    initialData = '[]',
    hiddenFieldName = "Form.EUProductUDI.ListOfStorageAndHandlingConditions",
    fieldTitle = "List of Storage and Handling Conditions",
    forceIsEditing = null,
    validationTrigger = 0
}) => {
    // Language Dictionary
    const languages = [
        { key: "en", value: "English" }, { key: "is", value: "Icelandic" },
        { key: "no", value: "Norwegian" }, { key: "tr", value: "Turkish" },
        { key: "bg", value: "Bulgarian" }, { key: "cs", value: "Czech" },
        { key: "da", value: "Danish" }, { key: "de", value: "German" },
        { key: "et", value: "Estonian" }, { key: "el", value: "Greek" },
        { key: "es", value: "Spanish" }, { key: "fr", value: "French" },
        { key: "hr", value: "Croatian" }, { key: "it", value: "Italian" },
        { key: "lv", value: "Latvian" }, { key: "lt", value: "Lithuanian" },
        { key: "hu", value: "Hungarian" }, { key: "mt", value: "Maltese" },
        { key: "nl", value: "Dutch" }, { key: "pl", value: "Polish" },
        { key: "pt", value: "Portuguese" }, { key: "ro", value: "Romanian" },
        { key: "sk", value: "Slovak" }, { key: "sl", value: "Slovenian" },
        { key: "fi", value: "Finnish" }, { key: "sv", value: "Swedish" },
        { key: "ga", value: "Irish" }
    ];

    // Main list state
    const [items, setItems] = useState(() => {
        try {
            return (typeof initialData === 'string' ? JSON.parse(initialData) : initialData) || [];
        } catch {
            return [];
        }
    });

    const [isEditing, setIsEditing] = useState(forceIsEditing != null ? forceIsEditing : false);
    // Staging state for the currently active form
    const [editingIndex, setEditingIndex] = useState(null);
    const [selectedType, setSelectedType] = useState({ Value: shcTypes[0].key, Text: shcTypes[0].value });

    // Staging state for the sub-table (Descriptions)
    const [currentDescriptions, setCurrentDescriptions] = useState([]);
    const [descLanguage, setDescLanguage] = useState({ Value: 'en', Text: 'English' });
    const [descText, setDescText] = useState('');

    // True when the selected type needs a Description before saving.
    const commentRequired = shcTypes.find(t => t.key === selectedType.Value)?.requiresComment ?? false;
    const missingRequiredComment = commentRequired && currentDescriptions.length === 0;

    // --- Handlers for Sub-Table (Descriptions) ---
    const handleAddDescription = () => {
        const newDesc = {
            Description: {
                Language: { ...descLanguage },
                Value: descText
            }
        };
        setCurrentDescriptions([...currentDescriptions, newDesc]);
        setDescText(''); // Reset textarea after adding
    };

    const handleRemoveDescription = (index) => {
        setCurrentDescriptions(currentDescriptions.filter((_, i) => i !== index));
    };

    const handleDescLanguageChange = (e) => {
        const value = e.target.value;
        const text = e.target.options[e.target.selectedIndex].text;
        setDescLanguage({ Value: value, Text: text });
    };

    // --- Handlers for Main Form ---
    const handleTypeChange = (e) => {
        const value = e.target.value;
        setSelectedType({ Value: value, Text: shcTypes.find(t => t.key === value)?.value ?? "" });
    };

    const handleSaveMain = () => {
        const mainItem = {
            Type: { ...selectedType },
            Descriptions: [...currentDescriptions]
        };

        const updatedItems = [...items];
        if (editingIndex !== null) {
            updatedItems[editingIndex] = mainItem;
        } else {
            updatedItems.push(mainItem);
        }

        setItems(updatedItems);
        updateGlobalContext(updatedItems);
        popup.close();
    };

    const handleEditMain = (index) => {
        const itemToEdit = items[index];
        setSelectedType(itemToEdit.Type);
        // Deep copy descriptions to avoid mutating the saved state while editing
        setCurrentDescriptions(JSON.parse(JSON.stringify(itemToEdit.Descriptions || [])));
        setEditingIndex(index);
        popup.openEdit();
    };

    const handleRemoveMain = (index) => {
        const updatedItems = items.filter((_, i) => i !== index);
        setItems(updatedItems);
        updateGlobalContext(updatedItems);
        resetMainForm();
    };

    const resetMainForm = () => {
        setSelectedType({ Value: shcTypes[0].key, Text: shcTypes[0].value });
        setCurrentDescriptions([]);
        setDescText('');
        setDescLanguage({ Value: 'en', Text: 'English' });
        setEditingIndex(null);
    };

    const popup = useUDIRowPopup(resetMainForm);

    const updateGlobalContext = (updatedItems) => {
        setUDI(prevItem => ({
            ...prevItem,
            ListOfStorageAndHandlingConditions: updatedItems
        }));
    };

    // Helper to format a description text correctly
    const formatDescriptionText = (descItem) => {
        let result = descItem.Description?.Value || "";
        if (result && descItem.Description?.Language?.Text) {
            result = `${descItem.Description.Language.Text}: ${result}`;
        }
        return result;
    };

    // Looked up live by Value (not stored) so old items display/validate the same as new ones.
    const shcTypeFor = (type) => shcTypes.find(t => t.key === type?.Value);
    const formatTypeLabel = (type) => {
        const match = shcTypeFor(type);
        return match ? `${match.value} - ${match.label}` : (type?.Text || "");
    };



    const changeIsEditingField = (state) => {

        if (forceIsEditing != null)
            return;

        setIsEditing(state);

    }


    useEffect(() => {

        if (forceIsEditing != null)
            setIsEditing(forceIsEditing);

    }, [forceIsEditing]);

    // Parent's Save failed validation for this field - auto-open the first problematic item into
    // the edit form so its "when editing" error shows, instead of only the generic banner far away.
    useEffect(() => {
        if (!validationTrigger)
            return;
        const missing = findMissingRequiredComment(items);
        if (missing.length > 0)
            handleEditMain(items.indexOf(missing[0]));
    }, [validationTrigger]);




    // --- Render Mode: Edit ---
    return (

        <div className="view-task-field">
            <div className="view-task-field-line">
                <div className="view-task-field-title">{fieldTitle}</div>
                <div className="view-task-field-value">

                    <div style={{ backgroundColor: '#f8f8f8', padding: '10px' }}>

                        {isEditing ?


                            <div style={{ backgroundColor: '#f8f8f8', padding: '10px' }}>

                                {/* MAIN TABLE */}
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Descriptions</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr
                                                key={index}
                                                className={editingIndex === index ? 'udi-complex-field-row-selected' : ''}
                                            >
                                                <td>{formatTypeLabel(item.Type)}</td>
                                                <td>
                                                    <ul>
                                                        {(item.Descriptions || []).map((desc, dIndex) => (
                                                            <li key={dIndex} style={{ whiteSpace: 'pre-wrap' }}>
                                                                {formatDescriptionText(desc)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                                <td>
                                                    <button type="button" className="udiEditButton" onClick={() => handleEditMain(index)}>
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button type="button" className="udiRemoveButton" onClick={() => handleRemoveMain(index)}>
                                                        <i className="fas fa-times-circle"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <UDIAddRowButton label="Add Storage And Handling Conditions" onClick={popup.openAdd} />

                                <UDIRowPopup
                                    isOpen={popup.isOpen}
                                    title={editingIndex !== null ? "Edit Storage And Handling Conditions" : "Add Storage And Handling Conditions"}
                                    saveLabel={editingIndex !== null ? "Save" : "Add"}
                                    onClose={popup.close}
                                    onSave={handleSaveMain}
                                    saveDisabled={missingRequiredComment}
                                >
                                    <UDIPopupField label="Type">
                                        <select value={selectedType.Value} onChange={handleTypeChange}>
                                            {shcTypes.map(type => <option key={type.key} value={type.key}>{type.value} - {type.label}</option>)}
                                        </select>
                                    </UDIPopupField>
                                    <UDIPopupField label={`Descriptions${commentRequired ? " *" : ""}`}>
                                        {missingRequiredComment && (
                                            <div><span className="form-field-error-message">
                                                {formatTypeLabel(selectedType)} requires a Description {selectedType.Text === "SHC099" ? "with free text explaining the condition" : "containing that value"}.
                                            </span></div>
                                        )}
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Description</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentDescriptions.map((desc, index) => (
                                                    <tr key={index}>
                                                        <td style={{ whiteSpace: 'pre-wrap' }}>{formatDescriptionText(desc)}</td>
                                                        <td>
                                                            <button type="button" className="udiRemoveButton" onClick={() => handleRemoveDescription(index)}>
                                                                <i className="fas fa-times-circle"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <select value={descLanguage.Value} onChange={handleDescLanguageChange} style={{ marginBottom: '5px' }}>
                                            {languages.map(lang => <option key={lang.key} value={lang.key}>{lang.value}</option>)}
                                        </select>
                                        <textarea value={descText} onChange={(e) => setDescText(e.target.value)} rows={2} />
                                        <input type="button" className="udiActionButton" value="Add Description" onClick={handleAddDescription} disabled={!descText.trim()} style={{ marginTop: '5px' }} />
                                    </UDIPopupField>
                                </UDIRowPopup>

                                <input type="hidden" name={hiddenFieldName} value={JSON.stringify(items)} />
                            </div>

                            :


                            <>

                                <table className="dataTable" style={{ borderWidth: '1px', width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Descriptions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr key={index}>
                                                <td>{formatTypeLabel(item.Type)}</td>
                                                <td>
                                                    <ul>
                                                        {(item.Descriptions || []).map((desc, dIndex) => (
                                                            <li key={dIndex} style={{ whiteSpace: 'pre-wrap' }}>
                                                                {formatDescriptionText(desc)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/*
						        <input
							        type="button"
							        className="udiActionButton"
							        value="Edit"
							        onClick={() => changeIsEditingField(true)}
						        />
                                */}


                            </>

                        }

                    </div>

                </div>
            </div>
        </div>


    );
};

export default EUStorageAndHandlingConditionsList;